import ErrorBoundary from '@/components/ErrorBoundary';
import { EMAIL_TEMPLATE_CONNECTOR_FLOW_NAME } from '@/features/campaigns/EmailCampaign/constants';
import { EmailTemplatePreviewModal } from '@/features/campaigns/EmailCampaign/templates/EmailTemplatePreviewModal';
import { HtmlEditorPortal } from '@/features/campaigns/EmailCampaign/templates/HtmlEditorPortal';
import type { EmailTemplateFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Email/TemplatePreview/EmailTemplatePreview';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { FlowMappingsForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/FlowMappingsForm';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData, FlowMappingsConfig } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { RouteConstants } from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { StepPageActions } from '@/global/components/Layout/StepPageActions';
import { StepPageActionsButton } from '@/global/components/Layout/StepPageActions/types';
import { ToastType } from '@/models/toast';
import HtmlEditor from '@/modules/HTML_EDITOR';
import { useCompanyValues } from '@/providers/CompanyProvider';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useWorkspace } from '@/features/workspaces/hooks/useWorkspace';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

const AUDIENCE_ID = 'a-JXX5hb38ZWJnKidR';

type CreationPhase = 'metadata' | 'design' | 'mappings';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type CreateTemplateOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

const hasMappingsUi = (mappingsConfig: FlowMappingsConfig | null) =>
  !!mappingsConfig?.schema?.properties && Object.keys(mappingsConfig.schema.properties).length > 0;

export const EmailTemplateCreation = () => {
  const { id: workspaceId } = useWorkspace();
  const { currentLanguage } = useCompanyValues();
  const [createFlowInstance] = useCreateFlowInstanceMutation();
  const appDispatch = useAppDispatch();
  const { t } = useTranslation('campaign');
  const { t: tAll } = useTranslation('ALL');
  const history = useHistory();

  const { data: connectorInstances, isLoading: isConnectorInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const { data: connectorFlows, isLoading: isConnectorFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId, queryParams: { showUserJourney: true } },
    { skip: !workspaceId }
  );

  const emailConnectorFlow = connectorFlows?.find((flow) => flow.name === EMAIL_TEMPLATE_CONNECTOR_FLOW_NAME);
  const emailConnectorFlowId = emailConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === emailConnectorFlow?.connectorId)?.id;

  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [phase, setPhase] = useState<CreationPhase>('metadata');
  const [formData, setFormData] = useState<FlowFormData>({});
  const [bodyHtml, setBodyHtml] = useState('');
  const [multiStepMappingsConfig, setMultiStepMappingsConfig] = useState<FlowMappingsConfig | null>(null);
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [isMappingsFormValid, setIsMappingsFormValid] = useState(false);
  const [previewFormData, setPreviewFormData] = useState<EmailTemplateFormData | null>(null);

  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });

  const showMappingsStep = hasMappingsUi(multiStepMappingsConfig);

  useEffect(() => {
    const fetchPrepareData = async () => {
      const response = await prepareStep({
        workspaceId,
        flowId: emailConnectorFlowId,
        audienceId: AUDIENCE_ID,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      const prepareResultResponse = translatedResponse?.configuration?.elements?.params;
      setPrepareResult(prepareResultResponse as PrepareStepResult);
    };
    if (!!connectorInstanceId && !!emailConnectorFlowId) {
      void fetchPrepareData();
    }
  }, [connectorInstanceId, emailConnectorFlowId, prepareStep, currentLanguage, workspaceId]);

  const buildFlowParams = useCallback(
    (overrides?: FlowFormData): FlowFormData => ({
      ...formData,
      ...overrides,
      template_body_html: overrides?.['template_body_html'] ?? bodyHtml,
    }),
    [formData, bodyHtml]
  );

  const handleCreateTemplate = async (opts?: CreateTemplateOpts) => {
    const flowParams = {
      ...(opts?.flowParams ?? buildFlowParams()),
      template_body_html: (opts?.flowParams?.['template_body_html'] as string | undefined) ?? bodyHtml,
    };
    const flowMappings = opts?.flowMappings ?? mappingData;

    if (!String(flowParams['template_body_html'] ?? '').trim()) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('emailWorkspace.templates.emailPreviewBodyPlaceholder')),
        })
      );
      setPhase('design');
      return { success: false, message: 'Missing template body' };
    }

    const mappingsUiRequired = hasMappingsUi(multiStepMappingsConfig);
    if (!opts?.fromFlowComplete && mappingsUiRequired && !isMappingsFormValid) {
      return { success: false, message: 'Invalid mappings' };
    }

    const instance = instances?.find((inst) => inst.connectorId === emailConnectorFlow?.connectorId);
    if (!instance) {
      const message = String(t('noConnectorInstanceFound'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: emailConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId: AUDIENCE_ID,
      flowParams,
      flowMappings,
    });

    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(
        showToast({
          title: 'Template created successfully',
          type: ToastType.SUCCESS,
        })
      );
      appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
      history.push(RouteConstants.emailTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('flowInstanceCreationFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const handleEditorSave = ({ name, html }: { name: string; html: string }) => {
    if (!html.trim()) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('emailWorkspace.templates.emailPreviewBodyPlaceholder')),
        })
      );
      return;
    }

    const nextFormData = { ...formData, ['template_name']: name };
    setFormData(nextFormData);
    setBodyHtml(html);

    if (hasMappingsUi(multiStepMappingsConfig)) {
      setPhase('mappings');
      return;
    }

    void handleCreateTemplate({
      flowParams: { ...nextFormData, template_body_html: html },
      flowMappings: {},
      fromFlowComplete: true,
    });
  };

  const cancelButton: StepPageActionsButton = {
    text: 'COMMON.cancel' as any,
    onClick: () => history.push(RouteConstants.emailTemplates),
  };

  const nextButton: StepPageActionsButton = {
    text: 'COMMON.confirm' as any,
    onClick: () =>
      void handleCreateTemplate({
        flowParams: buildFlowParams(),
        flowMappings: mappingData,
      }),
    disabled: !isMappingsFormValid,
  };

  const handleMappingDataChange = useCallback((data: FlowFormData) => {
    setMappingData(data);
  }, []);

  const handleMappingsValidationChange = useCallback((valid: boolean) => {
    setIsMappingsFormValid(valid);
  }, []);

  const handleFlowComplete = (allParams: FlowFormData, mappingsConfig: FlowMappingsConfig) => {
    setFormData(allParams);
    setMultiStepMappingsConfig(mappingsConfig);
    setMappingData({});
    setIsMappingsFormValid(false);
    setBodyHtml(typeof allParams['template_body_html'] === 'string' ? allParams['template_body_html'] : '');
    setPhase('design');
  };

  const isTemplateLoading = isConnectorFlowsLoading || isConnectorInstancesLoading || !prepareResult;

  if (phase === 'design') {
    return (
      <>
        <HtmlEditorPortal>
          <HtmlEditor
            templateName={String(formData['template_name'] ?? '')}
            value={bodyHtml}
            onChange={setBodyHtml}
            onTemplateNameChange={(name) => setFormData((prev) => ({ ...prev, ['template_name']: name }))}
            onClose={() => setPhase('metadata')}
            onPreview={(html) =>
              setPreviewFormData({
                ...(formData as EmailTemplateFormData),
                template_body_html: html,
              })
            }
            onSave={handleEditorSave}
          />
        </HtmlEditorPortal>
        {previewFormData ? <EmailTemplatePreviewModal formData={previewFormData} onClose={() => setPreviewFormData(null)} /> : null}
      </>
    );
  }

  return (
    <div className='space-y-4'>
      <h2 className='text-3xl font-semibold text-gray-900'>{t('emailWorkspace.templates.createEmailTemplate')}</h2>

      <div className={phase === 'metadata' ? undefined : 'hidden'}>
        <ErrorBoundary>
          {prepareResult ? (
            <MultiStepFlowForm
              showTemplatePreview
              templatePreviewVariant='email'
              htmlBodyFieldMode='external'
              lastStepActionLabel='Continue to email designer'
              audienceId={AUDIENCE_ID}
              connectorInstanceId={connectorInstanceId!}
              connectorFlowId={emailConnectorFlowId!}
              paramsConfig={prepareResult}
              onComplete={handleFlowComplete}
            />
          ) : (
            <p className='text-sm text-gray-600'>
              {isTemplateLoading ? t('emailWorkspace.templates.templateLoadingMessage') : t('emailWorkspace.templates.failedToLoadTemplate')}
            </p>
          )}
        </ErrorBoundary>
      </div>

      {phase === 'mappings' && showMappingsStep ? (
        <ErrorBoundary>
          <div className='space-y-4'>
            <h3 className='text-lg font-medium'>{tAll('destinations.steps.configureFlow.dataMapping')}</h3>
            <FlowMappingsForm
              config={multiStepMappingsConfig}
              formData={mappingData}
              onFormDataChange={handleMappingDataChange}
              onValidationChange={handleMappingsValidationChange}
            />
            <StepPageActions primaryButton={nextButton} secondaryButton={cancelButton} />
          </div>
        </ErrorBoundary>
      ) : null}
    </div>
  );
};
